import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import {
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Alert,
} from 'reactstrap';

import { fetchTrip, updateTripState } from '../../../actions/trips';
import { toast } from 'react-toastify';
import config from '../../../config';
import CustomerDetailsModal from '../../customers/CustomerDetailsModal';
import { getCustomerDisplayName } from '../../../utils/customerUtils';
import s from './TripDetails.module.scss';

/**
 * Ops "Update Trip State" — option values must match PostgreSQL `trip_status` enum tokens.
 *
 * Backend (driver/main.py update_trip_state): DB trigger notifies TripBot for driver +
 * assignment flows. `cancelled_by_customer` also runs payment adjustment; `timeout` from
 * `assigned` mirrors TimeoutManager column updates.
 */
const TRIP_STATE_OPTIONS = [
  { value: 'received', label: 'Received' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'en_route_pickup', label: 'En route to pickup' },
  { value: 'at_pickup', label: 'At pickup' },
  { value: 'loaded', label: 'Loaded' },
  { value: 'at_drop', label: 'At drop' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'cancelled_by_customer', label: 'Cancelled by customer' },
  { value: 'timeout', label: 'Timeout' },
];

const OPS_STATE_REDIRECT_STATUSES = ['cancelled', 'cancelled_by_customer', 'timeout'];

class TripDetails extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      isLoading: true,
      errors: {},
      stateModalOpen: false,
      pickupTimeModalOpen: false,
      labourPayModalOpen: false,
      markPaidModalOpen: false,
      newState: '',
      modifiedBy: 9001,
      reason: '',
      newPickupTime: '',
      tripPayAmount: '',
      labourPayAmount: '',
      isUpdatingLabourPay: false,
      isMarkingPaid: false,
      pickupTimeOverride: null,
      postponementMinutes: 0,
      // Bot control state
      botPaused: false,
      isTogglingBot: false,
      // Manual message state
      manualMessage: '',
      isSendingMessage: false,
      // Customer chat state
      customerChatHistory: [],
      customerConversationId: null,
      isLoadingCustomerChat: false,
      isSendingBookingSummary: false,
      isSendingDriverDetails: false,
      // Customer-notification preview modal (ops must preview before sending)
      customerPreviewModalOpen: false,
      customerPreviewType: null, // 'booking_summary' | 'driver_details'
      customerPreviewMessage: '',
      customerPreviewImageUrl: null,
      customerPreviewImageKind: null,
      customerPreviewChannel: null,
      isLoadingCustomerPreview: false,
      customerBotPaused: false,
      isTogglingCustomerBot: false,
      customerManualMessage: '',
      customerManualImage: null,
      isSendingCustomerMessage: false,
      customerHasActiveSession: false,
      customerDetailsModalOpen: false,
      // Whether booking_summary / driver_details have already gone out to
      // this customer (null = not fetched yet, renders no dot)
      notifStatus: { confirmationSent: null, driverDetailsSent: null },
    };
    
    this.redirectTimeout = null;
    this.customerManualImageInputRef = React.createRef();
  }

  async componentDidMount() {
    const tripId = this.props.match.params.id;
    
    try {
      await this.props.dispatch(fetchTrip(tripId));
      this.setState({ isLoading: false });
      
      // Fetch bot status for this trip
      this.fetchBotStatus();
      this.fetchCustomerChat();
      this.fetchNotifStatus();
      
      // Poll for bot status updates every 5 seconds
      this.botStatusInterval = setInterval(() => {
        this.fetchBotStatus();
      }, 5000);

      // Poll for customer chat updates every 10 seconds
      this.customerChatInterval = setInterval(() => {
        this.fetchCustomerChat(true);
      }, 10000);

      // Poll for driver chat updates every 10 seconds
      this.driverChatInterval = setInterval(() => {
        this.props.dispatch(fetchTrip(tripId));
      }, 10000);
    } catch (error) {
      console.error('Failed to fetch trip:', error);
      this.setState({ 
        isLoading: false,
        errors: { load: 'Failed to load trip details' }
      });
    }
  }

  fetchNotifStatus = async () => {
    const { currentTrip } = this.props;
    const bookingId = currentTrip?.booking_id;
    if (!bookingId) return;
    try {
      const res = await fetch(`${config.customerApiBase}/bookings/${bookingId}/customer-notification-status`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      this.setState({
        notifStatus: {
          confirmationSent: !!data.booking_confirmation_sent,
          driverDetailsSent: !!data.driver_details_sent,
        },
      });
    } catch (error) {
      console.error('Failed to fetch customer notification status:', error);
    }
  }

  fetchBotStatus = async () => {
    const tripId = this.props.match.params.id;
    const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
    
    try {
      const response = await fetch(`${config.opsApiBase}/trips/${tripId}/bot-status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        this.setState({ botPaused: data.bot_paused || false });
      }
    } catch (error) {
      console.error('Failed to fetch bot status:', error);
    }
  }

  handleToggleBot = async () => {
    const { botPaused } = this.state;
    const tripId = this.props.match.params.id;
    const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
    
    this.setState({ isTogglingBot: true });
    
    try {
      const response = await fetch(`${config.opsApiBase}/trips/${tripId}/bot-control`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: botPaused ? 'resume' : 'pause'
        })
      });
      
      if (response.ok) {
        this.setState({ 
          botPaused: !botPaused,
          isTogglingBot: false 
        });
        toast.success(botPaused ? 'Bot resumed. Automated responses enabled.' : 'Bot paused. Ops can now respond manually.');
      } else {
        throw new Error('Failed to toggle bot');
      }
    } catch (error) {
      console.error('Failed to toggle bot:', error);
      toast.error('Failed to update bot status. Please try again.');
      this.setState({ isTogglingBot: false });
    }
  }

  handleSendManualMessage = async () => {
    const { manualMessage } = this.state;
    const { currentTrip } = this.props;
    const tripId = this.props.match.params.id;
    const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
    
    if (!manualMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    this.setState({ isSendingMessage: true });
    
    try {
      const response = await fetch(`${config.opsApiBase}/trips/${tripId}/send-manual-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: manualMessage,
          driver_phone: currentTrip.driver_phone
        })
      });
      
      if (response.ok) {
        this.setState({ 
          manualMessage: '',
          isSendingMessage: false 
        });
        toast.success('Message sent successfully!');
        // Refresh trip data to get updated chat history
        await this.props.dispatch(fetchTrip(tripId));
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message. Please try again.');
      this.setState({ isSendingMessage: false });
    }
  }

  fetchCustomerChat = async (silent = false) => {
    const { currentTrip } = this.props;
    const bookingId = currentTrip?.booking_id;
    const customerId = currentTrip?.customer_id;
    const userId = currentTrip?.customer_user_id;
    if (!bookingId) return;

    if (!silent) {
      this.setState({ isLoadingCustomerChat: true });
    }

    const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
    const headers = { Authorization: `Bearer ${token}` };

    try {
      let customerBotPaused = this.state.customerBotPaused;
      let messages = [];
      let conversationId = null;
      let hasActiveSession = false;

      if (customerId) {
        const detailsResponse = await fetch(
          `${config.customerApiBase}/customers/${customerId}/details`,
          { headers }
        );
        if (detailsResponse.ok) {
          const detailsData = await detailsResponse.json();
          customerBotPaused = Boolean(detailsData.customer?.is_blocked);
        }
      }

      if (userId) {
        try {
          const sessionResponse = await fetch(
            `${config.customerApiBase}/sessions/${userId}/history`,
            { headers }
          );
          if (sessionResponse.ok) {
            const sessionData = await sessionResponse.json();
            const sessionMessages = sessionData.messages || sessionData.history || [];
            messages = sessionMessages.filter((msg) => {
              const content = (msg.content || msg.message || '').toString();
              return !content.startsWith('[TOOL]:') && !content.includes('<ToolCall>');
            });
            hasActiveSession = true;
          }
        } catch (sessionError) {
          console.debug('No active customer session:', sessionError);
        }
      }

      if (!hasActiveSession) {
        const response = await fetch(
          `${config.customerApiBase}/bookings/${bookingId}/conversation-messages`,
          { headers }
        );

        if (response.ok) {
          const data = await response.json();
          messages = data.messages || [];
          conversationId = data.conversation_id || null;
        } else if (!silent) {
          this.setState({ isLoadingCustomerChat: false });
          return;
        }
      }

      this.setState({
        customerChatHistory: messages,
        customerConversationId: conversationId,
        customerBotPaused,
        customerHasActiveSession: hasActiveSession,
        isLoadingCustomerChat: false,
      });
    } catch (error) {
      console.error('Failed to fetch customer chat:', error);
      if (!silent) {
        this.setState({ isLoadingCustomerChat: false });
      }
    }
  }

  handleToggleCustomerBot = async () => {
    const { customerBotPaused } = this.state;
    const { currentTrip } = this.props;
    const customerId = currentTrip?.customer_id;

    if (!customerId) {
      toast.error('Customer ID not available');
      return;
    }

    this.setState({ isTogglingCustomerBot: true });
    const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';

    try {
      const response = await fetch(
        `${config.customerApiBase}/customers/${customerId}/block`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ is_blocked: !customerBotPaused }),
        }
      );

      if (response.ok) {
        this.setState({
          customerBotPaused: !customerBotPaused,
          isTogglingCustomerBot: false,
        });
        toast.success(
          customerBotPaused
            ? 'Customer bot resumed. Automated responses enabled.'
            : 'Customer bot paused. You can now respond manually.'
        );
        await this.fetchCustomerChat(true);
      } else {
        throw new Error('Failed to toggle customer bot');
      }
    } catch (error) {
      console.error('Failed to toggle customer bot:', error);
      toast.error('Failed to update customer bot status. Please try again.');
      this.setState({ isTogglingCustomerBot: false });
    }
  }

  handleSendCustomerManualMessage = async () => {
    const { customerManualMessage, customerManualImage, customerConversationId, customerHasActiveSession, customerBotPaused } = this.state;
    const { currentTrip } = this.props;

    if (!customerBotPaused) {
      toast.error('Pause the customer bot first to send manual messages');
      return;
    }

    if (!customerManualMessage.trim() && !customerManualImage) {
      toast.error('Please enter a message');
      return;
    }

    this.setState({ isSendingCustomerMessage: true });
    const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';

    try {
      let convId = customerConversationId;
      const userId = currentTrip?.customer_user_id;

      if (!convId && !customerHasActiveSession && currentTrip?.customer_id && userId) {
        const createResponse = await fetch(`${config.customerApiBase}/conversations/create`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: userId,
            customer_id: currentTrip.customer_id,
            intent: 'ops_takeover',
            success: false,
            failure_reason: 'Ops initiated from trip page',
            source: 'trip_details',
          }),
        });

        if (createResponse.ok) {
          const createData = await createResponse.json();
          convId = createData.conversation_id;
          this.setState({ customerConversationId: convId });
        } else {
          throw new Error('Failed to create conversation');
        }
      }

      let endpoint;
      if (customerHasActiveSession && userId) {
        endpoint = `${config.customerApiBase}/sessions/${userId}/ops-message`;
      } else if (convId) {
        endpoint = `${config.customerApiBase}/conversations/${convId}/ops-message`;
      } else {
        throw new Error('No active session or conversation available');
      }

      let response;
      if (customerManualImage) {
        const formData = new FormData();
        if (customerManualMessage.trim()) formData.append('message', customerManualMessage);
        formData.append('file', customerManualImage.file, customerManualImage.name);
        response = await fetch(endpoint, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
      } else {
        response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ message: customerManualMessage }),
        });
      }

      if (response.ok) {
        this.setState({ customerManualMessage: '', customerManualImage: null, isSendingCustomerMessage: false });
        toast.success('Message sent to customer');
        await this.fetchCustomerChat(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send customer message:', error);
      toast.error(error.message || 'Failed to send message. Please try again.');
      this.setState({ isSendingCustomerMessage: false });
    }
  }

  // Both "Booking Summary" and "Driver Details" are now ops-triggered only
  // (never sent automatically). Clicking the button fetches a preview of the
  // exact message that will be sent; it is only sent once ops confirms it.
  CUSTOMER_PREVIEW_CONFIG = {
    booking_summary: {
      previewPath: 'preview-booking-summary',
      sendPath: 'send-booking-summary',
      title: 'Preview: Booking Summary',
      successMessage: 'Booking summary sent to customer',
      errorMessage: 'Failed to send booking summary',
    },
    driver_details: {
      previewPath: 'preview-driver-details',
      sendPath: 'send-driver-details',
      title: 'Preview: Driver Details',
      successMessage: 'Driver details sent to customer',
      errorMessage: 'Failed to send driver details',
    },
  };

  openCustomerSendPreview = async (previewType) => {
    const { currentTrip } = this.props;
    const bookingId = currentTrip?.booking_id;
    if (!bookingId) {
      toast.error('Booking ID not available');
      return;
    }

    const { previewPath, title } = this.CUSTOMER_PREVIEW_CONFIG[previewType];
    const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';

    this.setState({
      customerPreviewType: previewType,
      customerPreviewModalOpen: true,
      customerPreviewMessage: '',
      customerPreviewImageUrl: null,
      customerPreviewImageKind: null,
      customerPreviewChannel: null,
      isLoadingCustomerPreview: true,
    });

    try {
      const response = await fetch(
        `${config.customerApiBase}/bookings/${bookingId}/${previewPath}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to load ${title.toLowerCase()}`);
      }

      const data = await response.json();
      this.setState({
        customerPreviewMessage: data.message || '',
        customerPreviewImageUrl: data.photo_url || null,
        customerPreviewImageKind: data.image_kind || null,
        customerPreviewChannel: data.channel || null,
        isLoadingCustomerPreview: false,
      });
    } catch (error) {
      console.error('Failed to load preview:', error);
      toast.error(error.message || 'Failed to load preview');
      this.setState({ customerPreviewModalOpen: false, isLoadingCustomerPreview: false });
    }
  }

  closeCustomerSendPreview = () => {
    this.setState({
      customerPreviewModalOpen: false,
      customerPreviewType: null,
      customerPreviewMessage: '',
      customerPreviewImageUrl: null,
      customerPreviewImageKind: null,
      customerPreviewChannel: null,
    });
  }

  renderWhatsAppText = (text) => {
    if (!text) return 'No message content.';
    const parts = String(text).split(/(\*[^*\n]+\*)/g);
    return parts.map((part, index) => {
      if (part.length > 2 && part.startsWith('*') && part.endsWith('*')) {
        return <strong key={index}>{part.slice(1, -1)}</strong>;
      }
      return <span key={index}>{part}</span>;
    });
  }

  sentDotClass = (sent) => {
    if (sent === true) return s.customerActionBtnSent;
    if (sent === false) return s.customerActionBtnUnsent;
    return ''; // unknown / still loading - no dot yet
  }

  confirmCustomerSend = async () => {
    const { currentTrip } = this.props;
    const bookingId = currentTrip?.booking_id;
    const { customerPreviewType } = this.state;
    if (!bookingId || !customerPreviewType) return;

    const { sendPath, successMessage, errorMessage } = this.CUSTOMER_PREVIEW_CONFIG[customerPreviewType];
    const sendingStateKey = customerPreviewType === 'booking_summary' ? 'isSendingBookingSummary' : 'isSendingDriverDetails';
    const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';

    this.setState({ [sendingStateKey]: true });

    try {
      const response = await fetch(
        `${config.customerApiBase}/bookings/${bookingId}/${sendPath}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.ok) {
        toast.success(successMessage);
        this.closeCustomerSendPreview();
        this.setState((prev) => ({
          notifStatus: {
            confirmationSent: customerPreviewType === 'booking_summary' ? true : prev.notifStatus.confirmationSent,
            driverDetailsSent: customerPreviewType === 'driver_details' ? true : prev.notifStatus.driverDetailsSent,
          },
        }));
        await this.fetchCustomerChat(true);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorMessage);
      }
    } catch (error) {
      console.error(errorMessage, error);
      toast.error(error.message || errorMessage);
    } finally {
      this.setState({ [sendingStateKey]: false });
    }
  }

  componentWillUnmount() {
    if (this.redirectTimeout) {
      clearTimeout(this.redirectTimeout);
    }
    if (this.botStatusInterval) {
      clearInterval(this.botStatusInterval);
    }
    if (this.customerChatInterval) {
      clearInterval(this.customerChatInterval);
    }
    if (this.driverChatInterval) {
      clearInterval(this.driverChatInterval);
    }
  }

  toggleStateModal = () => {
    this.setState(prevState => ({
      stateModalOpen: !prevState.stateModalOpen,
      newState: '',
      reason: '',
    }));
  }

  togglePickupTimeModal = () => {
    this.setState(prevState => ({
      pickupTimeModalOpen: !prevState.pickupTimeModalOpen,
      newPickupTime: '',
      postponementMinutes: 0
    }));
  }

  toggleLabourPayModal = () => {
    const { currentTrip } = this.props;
    this.setState(prevState => ({
      labourPayModalOpen: !prevState.labourPayModalOpen,
      tripPayAmount: !prevState.labourPayModalOpen
        ? String(currentTrip?.estimated_cost ?? 0)
        : prevState.tripPayAmount,
      labourPayAmount: !prevState.labourPayModalOpen
        ? String(currentTrip?.labour_pay ?? 0)
        : prevState.labourPayAmount,
    }));
  }

  toggleMarkPaidModal = () => {
    this.setState(prevState => ({
      markPaidModalOpen: !prevState.markPaidModalOpen,
    }));
  }

  openCustomerDetailsModal = () => {
    const { currentTrip } = this.props;
    if (!currentTrip?.customer_id) {
      toast.error('Customer details not available for this trip');
      return;
    }
    this.setState({ customerDetailsModalOpen: true });
  }

  closeCustomerDetailsModal = () => {
    this.setState({ customerDetailsModalOpen: false });
  }

  handleUpdateState = async () => {
    const { newState, modifiedBy, reason } = this.state;
    const { currentTrip } = this.props;

    if (!newState || !reason) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const tripId = currentTrip.trip_id;
      const bookingId = currentTrip.booking_id;
      await this.props.dispatch(updateTripState(tripId, {
        new_state: newState,
        modified_by: modifiedBy,
        reason: reason,
      }));
      
      this.toggleStateModal();

      if (OPS_STATE_REDIRECT_STATUSES.includes(newState)) {
        const redirectMsg =
          newState === 'cancelled'
            ? 'Trip cancelled — customer notified, routing to unassigned bookings...'
            : 'Trip updated. Routing to unassigned bookings...';
        toast.success(redirectMsg);
        this.redirectTimeout = setTimeout(() => {
          this.props.history.push(`/app/operations/escalation?assignBooking=${bookingId}`);
        }, 1500);
      } else {
        await this.props.dispatch(fetchTrip(currentTrip.trip_id));
      }
    } catch (error) {
      console.error('Failed to update trip state:', error);
      toast.error('Failed to update trip state');
    }
  }

  handleUpdatePickupTime = async () => {
    const { newPickupTime } = this.state;
    const { currentTrip } = this.props;
    const tripId = currentTrip.trip_id;
    const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';

    if (!newPickupTime) {
      toast.error('Please select a new pickup time');
      return;
    }

    try {
      const response = await fetch(`${config.opsApiBase}/trips/${tripId}/pickup-time`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pickup_time: newPickupTime,
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update pickup time');
      }

      this.setState({ 
        pickupTimeOverride: newPickupTime,
        pickupTimeModalOpen: false,
        newPickupTime: ''
      });
      
      await this.props.dispatch(fetchTrip(tripId));
      toast.success('Pickup time updated successfully');
    } catch (error) {
      console.error('Error updating pickup time:', error);
      toast.error(error.message || 'Failed to update pickup time');
    }
  }

  handleUpdateLabourPay = async () => {
    const { tripPayAmount, labourPayAmount } = this.state;
    const tripId = this.props.match.params.id;
    const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';

    const parsedTripPay = Number(tripPayAmount);
    const parsedLabourPay = Number(labourPayAmount);

    if (Number.isNaN(parsedTripPay) || parsedTripPay < 0) {
      toast.error('Please enter a valid trip pay amount');
      return;
    }

    if (Number.isNaN(parsedLabourPay) || parsedLabourPay < 0) {
      toast.error('Please enter a valid labour pay amount');
      return;
    }

    this.setState({ isUpdatingLabourPay: true });

    try {
      const response = await fetch(`${config.opsApiBase}/trips/${tripId}/labour-pay`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          trip_pay: parsedTripPay,
          labour_pay: parsedLabourPay,
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update pay');
      }

      await this.props.dispatch(fetchTrip(tripId));
      this.setState({
        labourPayModalOpen: false,
        isUpdatingLabourPay: false
      });
      toast.success('Pay updated successfully');
    } catch (error) {
      console.error('Failed to update pay:', error);
      toast.error(error.message || 'Failed to update pay');
      this.setState({ isUpdatingLabourPay: false });
    }
  }

  handleMarkAsPaid = async (paymentMode) => {
    const tripId = this.props.match.params.id;
    const { currentTrip } = this.props;
    const token = localStorage.getItem('token') || 'mock-jwt-token-for-testing';
    const isTripPaid = Boolean(currentTrip?.has_paid || currentTrip?.is_paid_cash);

    if (isTripPaid) {
      toast.info('Trip is already marked as paid');
      return;
    }

    if (paymentMode !== 'cash' && paymentMode !== 'online') {
      toast.error('Please select a payment mode');
      return;
    }

    this.setState({ isMarkingPaid: true });

    try {
      const response = await fetch(`${config.opsApiBase}/trips/${tripId}/mark-paid`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_paid_cash: paymentMode === 'cash' })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to mark trip as paid');
      }

      await this.props.dispatch(fetchTrip(tripId));
      toast.success('Trip marked as paid');
      this.setState({
        isMarkingPaid: false,
        markPaidModalOpen: false,
      });
    } catch (error) {
      console.error('Failed to mark trip as paid:', error);
      toast.error(error.message || 'Failed to mark trip as paid');
      this.setState({ isMarkingPaid: false });
    }
  }

  getStatusClass = (status) => {
    const statusMap = {
      assigned: 'assigned',
      morning_confirmed: 'Accepted',
      night_confirmed: 'Accepted',
      en_route_pickup: 'en_route_pickup',
      at_pickup: 'at_pickup',
      loaded: 'loaded',
      trip_started: 'trip_started',
      en_route_drop: 'en_route_drop',
      completed: 'completed',
      trip_completed: 'trip_completed',
      cancelled: 'cancelled',
    };
    return statusMap[status] || '';
  }

  formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status.replace(/_/g, ' ');
  }

  formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  getDriverInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }

  getCustomerInitials = (name) => {
    if (!name) return 'C';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }

  getTripCustomerName = () => {
    const { currentTrip } = this.props;
    return getCustomerDisplayName(
      currentTrip?.customer_name,
      currentTrip?.customer_business_name,
      currentTrip?.customer_phone,
      currentTrip?.customer_id ? `Customer #${currentTrip.customer_id}` : 'Customer'
    );
  }

  getFlatChatHistory = (chatHistory) => {
    if (!chatHistory) return [];
    const flat = [];
    
    const processItem = (item) => {
      if (!item) return;
      
      // If it's a string, try to parse it
      if (typeof item === 'string') {
        try {
          const parsed = JSON.parse(item);
          processItem(parsed);
          return;
        } catch (e) {
          // Not valid JSON, add as plain text
          flat.push({ role: 'unknown', message: item });
          return;
        }
      }
      
      // If it's an array, recursively process each item
      if (Array.isArray(item)) {
        item.forEach(subItem => processItem(subItem));
        return;
      }
      
      // If it's an object
      if (typeof item === 'object') {
        // If it's a message object
        if (item.role || item.sender) {
          flat.push(item);
        } else if (item.messages && Array.isArray(item.messages)) {
          // Handle format: { messages: [...] }
          item.messages.forEach(subItem => processItem(subItem));
        } else {
          // Unknown object format, convert to string
          flat.push({ role: 'unknown', message: JSON.stringify(item) });
        }
      }
    };
    
    chatHistory.forEach(item => processItem(item));
    return flat;
  }

  parseMessageContent = (message) => {
    let content = message.message || message.content || message;
    const msgType = message.message_type || message.type || '';

    if (typeof content === 'string' && content.startsWith('BUTTON_CLICK:')) {
      const actionPart = content.split('|')[0].replace(/^BUTTON_CLICK:/, '');
      const actionKey = (actionPart.split(':').pop() || '').trim().toLowerCase();
      const labelMap = {
        accept_trip: 'Trip accepted',
        reject_trip: 'Trip rejected',
        got_it: 'Got it',
        reached_pickup: 'Reached pickup',
        reached_drop: 'Reached drop',
        loading_complete: 'Loading complete',
        unloading_complete: 'Unloading complete',
        work_completed: 'Work completed',
        work_completed_confirmed: 'Work completed (confirmed)',
      };
      content = labelMap[actionKey]
        || actionKey.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }
    
    // Check for audio
    if (msgType === 'audio' || (typeof content === 'string' && (content.startsWith('audio:') || content.includes('|transcript:')))) {
      let audioUrl = '';
      let transcriptText = '';
      if (typeof content === 'string') {
        if (content.includes('|transcript:')) {
          const parts = content.split('|transcript:');
          audioUrl = parts[0].replace('audio:', '').trim();
          transcriptText = parts[1].trim();
        } else {
          audioUrl = content.replace('audio:', '').trim();
        }
      }
      return {
        type: 'audio',
        text: transcriptText ? `🎤 "${transcriptText}"` : '🎤 Voice Note',
        url: audioUrl
      };
    }
    
    // Check for media_id based image messages (ops media uploads)
    if (msgType === 'image' && message.media_id) {
      return {
        type: 'image',
        text: typeof content === 'string' && content !== '[Image]' ? content : '',
        url: `${config.customerApiBase}/media/${message.media_id}`
      };
    }

    // Check for photo
    if (content.includes('📸') || content.includes('Photo:') || content.includes('lookaside.fbsbx.com')) {
      const urlMatch = content.match(/(https?:\/\/[^\s]+)/);
      return {
        type: 'photo',
        text: content.replace(/(https?:\/\/[^\s]+)/g, '').replace('📸', '').replace('Photo:', '').trim(),
        url: urlMatch ? urlMatch[1] : null
      };
    }
    
    // Check for location - handles formats like "location 13.351871794068 74.79182895273" or "📍 Location:"
    if (content.includes('📍') || content.includes('Location:') || content.toLowerCase().startsWith('location ')) {
      // Match coordinates in various formats
      const coordMatch = content.match(/(-?\d+\.\d+)[\s,]+(-?\d+\.\d+)/);
      if (coordMatch) {
        return {
          type: 'location',
          text: `📍 Location shared`,
          coords: { lat: coordMatch[1], lng: coordMatch[2] }
        };
      }
    }
    
    // Check for map link
    if (content.includes('google.com/maps')) {
      const urlMatch = content.match(/(https?:\/\/[^\s]+)/);
      return {
        type: 'map',
        text: content.replace(/(https?:\/\/[^\s]+)/g, '').trim(),
        url: urlMatch ? urlMatch[1] : null
      };
    }
    
    return { type: 'text', text: content };
  }

  // Parse chat message - handles both object and JSON string formats
  parseChatMessage = (msg) => {
    if (!msg) return null;
    
    // If it's already an object with role/message, return as-is
    if (typeof msg === 'object' && (msg.role || msg.sender)) {
      return msg;
    }
    
    // If it's a string, try to parse as JSON
    if (typeof msg === 'string') {
      try {
        const parsed = JSON.parse(msg);
        if (parsed && (parsed.role || parsed.sender)) {
          return parsed;
        }
      } catch (e) {
        // Not valid JSON, treat as plain text message
        return { role: 'unknown', message: msg };
      }
    }
    
    return { role: 'unknown', message: String(msg) };
  }

  renderChatMessage = (rawMessage, index) => {
    const message = this.parseChatMessage(rawMessage);
    if (!message) return null;
    
    const sender = (message.sender || message.role || '').toLowerCase();
    const isBot = sender === 'bot' || sender === 'assistant' || sender === 'system';
    const isDriver = sender === 'driver' || sender === 'user';
    
    const { currentTrip } = this.props;
    const parsed = this.parseMessageContent(message);
    const timestamp = message.timestamp || message.time;
    
    return (
      <div key={index} className={`${s.message} ${isBot ? s.botMessage : s.driverMessage}`}>
        <div className={s.messageAvatar}>
          {isBot ? '🤖' : this.getDriverInitials(currentTrip?.driver_name)}
        </div>
        <div className={s.messageContent}>
          <div className={s.messageText}>
            {parsed.text}
            
            {parsed.type === 'photo' && parsed.url && (
              <div className={s.messageAttachment}>
                <div className={s.attachmentPreview}>
                  <i className="fa fa-camera" />
                  <span>Photo shared</span>
                </div>
              </div>
            )}
            
            {parsed.type === 'audio' && parsed.url && (
              <div className={s.messageAttachment} style={{ marginTop: '8px' }}>
                <audio 
                  controls 
                  src={parsed.url} 
                  style={{ width: '100%', height: '32px', borderRadius: '4px' }} 
                />
              </div>
            )}
            
            {parsed.type === 'location' && parsed.coords && (
              <div className={s.locationAttachment}>
                <a 
                  href={`https://www.google.com/maps?q=${parsed.coords.lat},${parsed.coords.lng}`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className={s.locationPreview}
                >
                  <i className="fa fa-map-marker" />
                  <span>📍 View Location</span>
                </a>
              </div>
            )}
            
            {parsed.type === 'map' && parsed.url && (
              <div className={s.messageAttachment}>
                <a href={parsed.url} target="_blank" rel="noopener noreferrer" className={s.attachmentPreview}>
                  <i className="fa fa-map" />
                  <span>View on Maps</span>
                </a>
              </div>
            )}
          </div>
          {timestamp && (
            <span className={s.messageTime}>
              {new Date(timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>
    );
  }

  renderCustomerChatMessage = (rawMessage, index) => {
    const message = this.parseChatMessage(rawMessage);
    if (!message) return null;

    const sender = (message.sender || message.role || '').toLowerCase();
    const isCustomer = sender === 'user';

    const { currentTrip } = this.props;
    const parsed = this.parseMessageContent(message);
    const timestamp = message.timestamp || message.time;

    return (
      <div
        key={index}
        className={`${s.message} ${isCustomer ? s.customerMessage : s.botMessage}`}
      >
        <div className={s.messageAvatar}>
          {isCustomer
            ? this.getCustomerInitials(this.getTripCustomerName())
            : (sender === 'operator' ? '👤' : '🤖')}
        </div>
        <div className={s.messageContent}>
          <div className={s.messageText}>
            {parsed.type === 'image' && parsed.url && (
              <img
                src={parsed.url}
                alt={parsed.text || 'Image'}
                style={{ maxWidth: '100%', borderRadius: 8, display: 'block', cursor: 'pointer' }}
                onClick={() => window.open(parsed.url, '_blank')}
              />
            )}
            {parsed.text}
          </div>
          {timestamp && (
            <span className={s.messageTime}>
              {new Date(timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>
    );
  }

  handleCustomerManualImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      this.setState({
        customerManualImage: {
          file,
          name: file.name,
          previewUrl: URL.createObjectURL(file)
        }
      });
    }
    e.target.value = '';
  }

  handleRemoveCustomerManualImage = () => {
    this.setState({ customerManualImage: null });
  }

  handleAttachCustomerManualImage = () => {
    this.customerManualImageInputRef?.click();
  }

  render() {
    const { isLoading, errors, isMarkingPaid } = this.state;
    const { currentTrip, isFetching } = this.props;

    if (isLoading || isFetching) {
      return (
        <div className={s.tripDetailsContainer}>
          <Breadcrumb>
            <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
            <BreadcrumbItem active>Trip Details</BreadcrumbItem>
          </Breadcrumb>
          
          <div className={s.loadingState}>
            <div className={s.spinner} />
            <p>Loading trip details...</p>
          </div>
        </div>
      );
    }

    if (errors.load || !currentTrip) {
      return (
        <div className={s.tripDetailsContainer}>
          <Breadcrumb>
            <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
            <BreadcrumbItem active>Trip Details</BreadcrumbItem>
          </Breadcrumb>
          
          <div className={s.errorState}>
            <div className={s.errorIcon}>
              <i className="fa fa-exclamation-circle" />
            </div>
            <h3>Trip Not Found</h3>
            <p>{errors.load || 'The requested trip could not be found.'}</p>
            <Button className={s.backButton} onClick={() => this.props.history.push('/app/trips')}>
              <i className="fa fa-arrow-left mr-2" />
              Back to Trips
            </Button>
          </div>
        </div>
      );
    }

    const chatHistory = this.getFlatChatHistory(currentTrip.chat_history);
    const customerChatHistory = this.getFlatChatHistory(this.state.customerChatHistory);
    const isTripPaid = Boolean(currentTrip.has_paid || currentTrip.is_paid_cash);

    return (
      <div className={s.tripDetailsContainer}>
        <Breadcrumb>
          <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
          <BreadcrumbItem active>Trip Details</BreadcrumbItem>
        </Breadcrumb>

        {/* Page Header */}
        <div className={s.pageHeader}>
          <div className={s.pageTitle}>
            <div className={s.titleIcon}>
              <i className="fa fa-map-marker" />
            </div>
            <h1>Trip Details</h1>
            <span className={s.bookingBadge}>#{currentTrip.booking_id}</span>
          </div>
          <div className={s.actionButtons}>
            <button className={`${s.actionBtn} ${s.btnUpdateState}`} onClick={this.toggleStateModal}>
              <i className="fa fa-edit" />
              Update State
            </button>
            <button className={`${s.actionBtn} ${s.btnPickupTime}`} onClick={this.togglePickupTimeModal}>
              <i className="fa fa-clock-o" />
              Change Date Time of Trip
            </button>
            <button className={`${s.actionBtn} ${s.btnLabourPay}`} onClick={this.toggleLabourPayModal}>
              <i className="fa fa-inr" />
              Add Pay
            </button>
            <button
              className={`${s.actionBtn} ${s.btnMarkPaid}`}
              onClick={this.toggleMarkPaidModal}
              disabled={isMarkingPaid || isTripPaid}
            >
              <i className="fa fa-check-circle" />
              {isTripPaid
                ? (currentTrip.is_paid_cash ? 'Paid via Cash' : 'Paid via Online')
                : (isMarkingPaid ? 'Marking...' : 'Mark as Paid')}
            </button>
          </div>
        </div>

        {/* Content - Trip info + Communications */}
        <div className={s.contentWrapper}>
          {/* Left Panel - Trip Details */}
          <div className={s.detailsColumn}>
          <div className={s.detailsPanel}>
            <div className={s.panelHeader}>
              <h2><i className="fa fa-info-circle" /> Trip Information</h2>
            </div>
            <div className={s.panelContent}>
              {/* Basic Info */}
              <div className={s.detailSection}>
                <div className={s.sectionTitle}>Basic Information</div>
                <div className={s.detailGrid}>
                  <div className={s.detailItem}>
                    <div className={s.detailLabel}>Trip ID</div>
                    <div className={s.detailValue}>{currentTrip.trip_id}</div>
                  </div>
                  <div className={s.detailItem}>
                    <div className={s.detailLabel}>Booking ID</div>
                    <div className={s.detailValue}>{currentTrip.booking_id}</div>
                  </div>
                  <div className={s.detailItem}>
                    <div className={s.detailLabel}>Status</div>
                    <div className={s.detailValue}>
                      <span className={`${s.statusBadge} ${s[this.getStatusClass(currentTrip.status)]}`}>
                        {this.formatStatus(currentTrip.status)}
                      </span>
                    </div>
                  </div>
                  <div className={s.detailItem}>
                    <div className={s.detailLabel}>Driver</div>
                    <div className={s.detailValue}>{currentTrip.driver_name || `ID: ${currentTrip.driver_id}` || 'Not assigned'}</div>
                  </div>
                  <div className={s.detailItem}>
                    <div className={s.detailLabel}>Vehicle</div>
                    <div className={s.detailValue}>
                      {currentTrip.vehicle_id ? (
                        <Link to={`/app/dispatch/vehicles?tab=vehicles&search=${currentTrip.vehicle_id}`}>
                          Vehicle #{currentTrip.vehicle_id}
                        </Link>
                      ) : (
                        <span style={{ color: currentTrip.driver_id ? '#dc2626' : '#6b7280', fontStyle: currentTrip.driver_id ? 'italic' : 'normal' }}>
                          {currentTrip.driver_id ? 'Vehicle deleted' : 'Not assigned'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className={`${s.detailItem} ${s.fullWidth}`}>
                    <div className={s.detailLabel}>Customer</div>
                    <div className={s.detailValue}>
                      {currentTrip.customer_id ? (
                        <button
                          type="button"
                          className={s.customerDetailsLink}
                          onClick={this.openCustomerDetailsModal}
                          title="View customer details"
                        >
                          <span>{this.getTripCustomerName()}</span>
                          {currentTrip.customer_phone && (
                            <span className={s.customerPhoneMuted}> · {currentTrip.customer_phone}</span>
                          )}
                          <i className="fa fa-external-link" />
                        </button>
                      ) : (
                        'Not linked'
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Locations */}
              <div className={s.detailSection}>
                <div className={s.sectionTitle}>Route Details</div>
                <div className={s.locationCard}>
                  <div className={s.locationHeader}>
                    <span className={s.locationIcon}>📍</span>
                    <span className={s.locationType}>Pickup Location</span>
                  </div>
                  <div className={s.locationAddress}>{currentTrip.pickup_location || 'Not specified'}</div>
                </div>
                <div className={s.locationCard}>
                  <div className={s.locationHeader}>
                    <span className={s.locationIcon}>🎯</span>
                    <span className={s.locationType}>Drop Location</span>
                  </div>
                  <div className={s.locationAddress}>{currentTrip.drop_location || 'Not specified'}</div>
                </div>
              </div>

              {/* Distance & Time */}
              <div className={s.detailSection}>
                <div className={s.sectionTitle}>Distance & Time</div>
                <div className={s.detailGrid}>
                  <div className={s.detailItem}>
                    <div className={s.detailLabel}>Active KM</div>
                    <div className={s.detailValue}>{currentTrip.active_km || 0} km</div>
                  </div>
                  <div className={s.detailItem}>
                    <div className={s.detailLabel}>Dead KM</div>
                    <div className={s.detailValue}>{currentTrip.total_dead_km || 0} km</div>
                  </div>
                  <div className={`${s.detailItem} ${s.fullWidth}`}>
                    <div className={s.detailLabel}>Pickup Time</div>
                    <div className={s.detailValue}>{this.formatDateTime(currentTrip.pickup_time)}</div>
                  </div>
                  <div className={`${s.detailItem} ${s.fullWidth}`}>
                    <div className={s.detailLabel}>Labour Pay</div>
                    <div className={s.detailValue}>₹ {Number(currentTrip.labour_pay || 0).toFixed(2)}</div>
                  </div>
                  <div className={`${s.detailItem} ${s.fullWidth}`}>
                    <div className={s.detailLabel}>Payment Status</div>
                    <div className={s.detailValue}>
                      {isTripPaid
                        ? (currentTrip.is_paid_cash ? 'Paid (Cash)' : 'Paid (Online)')
                        : 'Unpaid'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Trip Progress */}
              <div className={s.detailSection}>
                <div className={s.sectionTitle}>Trip Progress</div>
                <div className={s.progressItem}>
                  <div className={`${s.progressIcon} ${currentTrip.pickup_reached_time ? s.completed : s.pending}`}>
                    <i className={`fa ${currentTrip.pickup_reached_time ? 'fa-check' : 'fa-clock-o'}`} />
                  </div>
                  <div className={s.progressInfo}>
                    <div className={s.progressLabel}>Pickup Reached</div>
                    <div className={s.progressTime}>
                      {currentTrip.pickup_reached_time ? this.formatDateTime(currentTrip.pickup_reached_time) : 'Not reached'}
                    </div>
                  </div>
                </div>
                <div className={s.progressItem}>
                  <div className={`${s.progressIcon} ${currentTrip.trip_started_at ? s.completed : s.pending}`}>
                    <i className={`fa ${currentTrip.trip_started_at ? 'fa-check' : 'fa-clock-o'}`} />
                  </div>
                  <div className={s.progressInfo}>
                    <div className={s.progressLabel}>Trip Started</div>
                    <div className={s.progressTime}>
                      {currentTrip.trip_started_at ? this.formatDateTime(currentTrip.trip_started_at) : 'Not started'}
                    </div>
                  </div>
                </div>
                <div className={s.progressItem}>
                  <div className={`${s.progressIcon} ${currentTrip.completed_at ? s.completed : s.pending}`}>
                    <i className={`fa ${currentTrip.completed_at ? 'fa-check' : 'fa-clock-o'}`} />
                  </div>
                  <div className={s.progressInfo}>
                    <div className={s.progressLabel}>Trip Completed</div>
                    <div className={s.progressTime}>
                      {currentTrip.completed_at ? this.formatDateTime(currentTrip.completed_at) : 'Not completed'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>

          {/* Right - Live Communications */}
          <div className={s.communicationsPanel}>
            <div className={s.communicationsHeader}>
              <div>
                <h2><i className="fa fa-headphones" /> Live Communications</h2>
                <p>Manage driver and customer conversations for this trip</p>
              </div>
            </div>
            <div className={s.chatsGrid}>
          {/* Driver Chat Panel */}
          <div className={`${s.chatPanel} ${s.driverChatPanel}`}>
            <div className={s.chatHeader}>
              <div>
                <h2>
                  <i className="fa fa-comments" />
                  Driver Conversation
                </h2>
                <div className={s.chatSubtitle}>
                  <div className={s.driverInfo}>
                    <div className={s.driverAvatar}>
                      {this.getDriverInitials(currentTrip.driver_name)}
                    </div>
                    <span>{currentTrip.driver_name || 'Driver'}</span>
                  </div>
                  <span>•</span>
                  <span>Trip #{currentTrip.trip_id}</span>
                </div>
              </div>
              <div className={s.botControls}>
                {this.state.botPaused ? (
                  <div className={s.botStatus}>
                    <span className={s.statusIndicator} style={{ backgroundColor: '#ffc107' }}>
                      <i className="fa fa-pause" />
                    </span>
                    <span className={s.statusText}>Manual Mode</span>
                  </div>
                ) : (
                  <div className={s.botStatus}>
                    <span className={s.statusIndicator} style={{ backgroundColor: '#28a745' }}>
                      <i className="fa fa-robot" />
                    </span>
                    <span className={s.statusText}>Bot Active</span>
                  </div>
                )}
                <Button
                  color={this.state.botPaused ? 'success' : 'warning'}
                  size="sm"
                  onClick={this.handleToggleBot}
                  disabled={this.state.isTogglingBot}
                  className={s.botToggleBtn}
                >
                  {this.state.isTogglingBot ? (
                    <>
                      <i className="fa fa-spinner fa-spin" /> Loading...
                    </>
                  ) : this.state.botPaused ? (
                    <>
                      <i className="fa fa-play" /> Resume Bot
                    </>
                  ) : (
                    <>
                      <i className="fa fa-pause" /> Pause Bot
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            <div className={s.chatMessages}>
              {chatHistory.length === 0 ? (
                <div className={s.emptyChat}>
                  <div className={s.emptyChatIcon}>
                    <i className="fa fa-comments-o" />
                  </div>
                  <h3>No Conversation Yet</h3>
                  <p>Chat history will appear here once the driver starts communicating.</p>
                </div>
              ) : (
                <div className={s.messageGroup}>
                  {chatHistory.map((message, index) => this.renderChatMessage(message, index))}
                </div>
              )}
            </div>
            
            <div className={s.chatFooter}>
              {this.state.botPaused ? (
                <div className={s.manualMessageInput}>
                  <Input
                    type="textarea"
                    placeholder="Type your message to the driver..."
                    value={this.state.manualMessage}
                    onChange={(e) => this.setState({ manualMessage: e.target.value })}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        this.handleSendManualMessage();
                      }
                    }}
                    disabled={this.state.isSendingMessage}
                    rows="2"
                    className={s.messageTextarea}
                  />
                  <Button
                    color="primary"
                    onClick={this.handleSendManualMessage}
                    disabled={this.state.isSendingMessage || !this.state.manualMessage.trim()}
                    className={s.sendButton}
                  >
                    {this.state.isSendingMessage ? (
                      <>
                        <i className="fa fa-spinner fa-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-paper-plane" /> Send Message
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className={s.messageCount}>
                  <i className="fa fa-comment" />
                  <span>{chatHistory.length} message{chatHistory.length !== 1 ? 's' : ''} in this conversation</span>
                </div>
              )}
            </div>
          </div>

          {/* Customer Chat Panel */}
          <div className={`${s.chatPanel} ${s.customerChatPanel}`}>
            <div className={`${s.chatHeader} ${s.customerChatHeader}`}>
              <div>
                <h2>
                  <i className="fa fa-user" />
                  Customer Conversation
                </h2>
                <div className={s.chatSubtitle}>
                  <div className={s.driverInfo}>
                    <button
                      type="button"
                      className={s.chatParticipantLink}
                      onClick={this.openCustomerDetailsModal}
                      disabled={!currentTrip.customer_id}
                      title={currentTrip.customer_id ? 'View customer details' : 'Customer not linked'}
                    >
                      <div className={`${s.driverAvatar} ${s.customerAvatar}`}>
                        {this.getCustomerInitials(this.getTripCustomerName())}
                      </div>
                      <span>{this.getTripCustomerName()}</span>
                    </button>
                  </div>
                  {currentTrip.customer_phone && (
                    <>
                      <span>•</span>
                      <span>{currentTrip.customer_phone}</span>
                    </>
                  )}
                  {currentTrip.customer_id && (
                    <button
                      type="button"
                      className={s.viewDetailsBtn}
                      onClick={this.openCustomerDetailsModal}
                      title="View customer details"
                    >
                      <i className="fa fa-id-card" /> Details
                    </button>
                  )}
                </div>
              </div>
              <div className={s.botControls}>
                {this.state.customerBotPaused ? (
                  <div className={s.botStatus}>
                    <span className={s.statusIndicator} style={{ backgroundColor: '#ffc107' }}>
                      <i className="fa fa-pause" />
                    </span>
                    <span className={s.statusText}>Manual Mode</span>
                  </div>
                ) : (
                  <div className={s.botStatus}>
                    <span className={s.statusIndicator} style={{ backgroundColor: '#28a745' }}>
                      <i className="fa fa-robot" />
                    </span>
                    <span className={s.statusText}>Bot Active</span>
                  </div>
                )}
                <Button
                  color={this.state.customerBotPaused ? 'success' : 'warning'}
                  size="sm"
                  onClick={this.handleToggleCustomerBot}
                  disabled={this.state.isTogglingCustomerBot || !currentTrip.customer_id}
                  className={s.botToggleBtn}
                >
                  {this.state.isTogglingCustomerBot ? (
                    <>
                      <i className="fa fa-spinner fa-spin" /> Loading...
                    </>
                  ) : this.state.customerBotPaused ? (
                    <>
                      <i className="fa fa-play" /> Resume Bot
                    </>
                  ) : (
                    <>
                      <i className="fa fa-pause" /> Pause Bot
                    </>
                  )}
                </Button>
              </div>
            </div>

            <div className={s.chatMessages}>
              {this.state.isLoadingCustomerChat ? (
                <div className={s.emptyChat}>
                  <div className={s.spinner} />
                  <p>Loading customer chat...</p>
                </div>
              ) : customerChatHistory.length === 0 ? (
                <div className={s.emptyChat}>
                  <div className={s.emptyChatIcon}>
                    <i className="fa fa-comments-o" />
                  </div>
                  <h3>No Customer Messages Yet</h3>
                  <p>Customer chat history will appear here once messages are exchanged.</p>
                </div>
              ) : (
                <div className={s.messageGroup}>
                  {customerChatHistory.map((message, index) => this.renderCustomerChatMessage(message, index))}
                </div>
              )}
            </div>

            <div className={s.chatFooter}>
              <div className={s.customerActionButtons}>
                <Button
                  color="info"
                  size="sm"
                  onClick={() => this.openCustomerSendPreview('booking_summary')}
                  disabled={this.state.isSendingBookingSummary || this.state.isSendingDriverDetails}
                  className={`${s.customerActionBtn} ${this.sentDotClass(this.state.notifStatus.confirmationSent)}`}
                  title={this.state.notifStatus.confirmationSent ? 'Already sent to customer' : 'Not sent yet'}
                >
                  {this.state.isSendingBookingSummary ? (
                    <>
                      <i className="fa fa-spinner fa-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-clipboard" /> Booking Summary
                    </>
                  )}
                </Button>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => this.openCustomerSendPreview('driver_details')}
                  disabled={this.state.isSendingBookingSummary || this.state.isSendingDriverDetails || !currentTrip.driver_name}
                  className={`${s.customerActionBtn} ${this.sentDotClass(this.state.notifStatus.driverDetailsSent)}`}
                  title={this.state.notifStatus.driverDetailsSent ? 'Already sent to customer' : 'Not sent yet'}
                >
                  {this.state.isSendingDriverDetails ? (
                    <>
                      <i className="fa fa-spinner fa-spin" /> Sending...
                    </>
                  ) : (
                    <>
                      <i className="fa fa-id-card" /> Driver Details
                    </>
                  )}
                </Button>
              </div>
              {this.state.customerBotPaused ? (
                <div className={s.manualMessageInput}>
                  {this.state.customerManualImage && (
                    <div className={s.imagePreview}>
                      <img
                        src={this.state.customerManualImage.previewUrl}
                        alt=""
                        className={s.imagePreviewImg}
                      />
                      <span className={s.imagePreviewName}>{this.state.customerManualImage.name}</span>
                      <button
                        className={s.imagePreviewRemove}
                        onClick={this.handleRemoveCustomerManualImage}
                        title="Remove image"
                      >
                        <i className="fa fa-times" />
                      </button>
                    </div>
                  )}
                  <Input
                    type="textarea"
                    placeholder="Type your message to the customer..."
                    value={this.state.customerManualMessage}
                    onChange={(e) => this.setState({ customerManualMessage: e.target.value })}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        this.handleSendCustomerManualMessage();
                      }
                    }}
                    disabled={this.state.isSendingCustomerMessage}
                    rows="2"
                    className={s.messageTextarea}
                  />
                  <input
                    ref={this.customerManualImageInputRef}
                    type="file"
                    accept="image/*"
                    onChange={this.handleCustomerManualImageChange}
                    style={{ display: 'none' }}
                  />
                  <Button
                    color="secondary"
                    onClick={this.handleAttachCustomerManualImage}
                    disabled={this.state.isSendingCustomerMessage}
                    className={s.attachImageBtn}
                    title="Attach image"
                  >
                    <i className="fa fa-paperclip" />
                  </Button>
                  <Button
                    color="primary"
                    onClick={this.handleSendCustomerManualMessage}
                    disabled={this.state.isSendingCustomerMessage || (!this.state.customerManualMessage.trim() && !this.state.customerManualImage)}
                    className={s.sendButton}
                  >
                    {this.state.isSendingCustomerMessage ? (
                      <>
                        <i className="fa fa-spinner fa-spin" /> Sending...
                      </>
                    ) : (
                      <>
                        <i className="fa fa-paper-plane" /> Send Message
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className={s.messageCount}>
                  <i className="fa fa-comment" />
                  <span>{customerChatHistory.length} message{customerChatHistory.length !== 1 ? 's' : ''} in this conversation</span>
                </div>
              )}
            </div>
          </div>
            </div>
          </div>
        </div>

        {/* Update State Modal */}
        <Modal isOpen={this.state.stateModalOpen} toggle={this.toggleStateModal}>
          <ModalHeader toggle={this.toggleStateModal}>Update Trip State</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="newState">New State *</Label>
              <Input
                type="select"
                name="newState"
                id="newState"
                value={this.state.newState}
                onChange={(e) => this.setState({ newState: e.target.value })}
              >
                <option value="">Select State</option>
                {TRIP_STATE_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Input>
            </FormGroup>

            <FormGroup>
              <Label for="reason">Reason *</Label>
              <Input
                type="textarea"
                name="reason"
                id="reason"
                placeholder="Enter reason for state change"
                rows="3"
                value={this.state.reason}
                onChange={(e) => this.setState({ reason: e.target.value })}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={this.handleUpdateState}>
              Update State
            </Button>
            {' '}
            <Button color="secondary" onClick={this.toggleStateModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        {/* Change Date Time of Trip Modal */}
        <Modal isOpen={this.state.pickupTimeModalOpen} toggle={this.togglePickupTimeModal}>
          <ModalHeader toggle={this.togglePickupTimeModal}>Change Date Time of Trip</ModalHeader>
          <ModalBody>
            <Alert color="info">
              <strong>Current Pickup Time:</strong>{' '}
              {this.state.pickupTimeOverride || currentTrip.pickup_time
                ? this.formatDateTime(this.state.pickupTimeOverride || currentTrip.pickup_time)
                : 'N/A'}
            </Alert>
            <FormGroup>
              <Label for="newPickupTime">New Pickup Time *</Label>
              <Input
                type="datetime-local"
                name="newPickupTime"
                id="newPickupTime"
                value={this.state.newPickupTime}
                onChange={(e) => this.setState({ newPickupTime: e.target.value })}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button 
              color="success" 
              onClick={() => this.handleUpdatePickupTime()}
              disabled={!this.state.newPickupTime}
            >
              <i className="fa fa-clock-o mr-1" />
              Change Pickup Time
            </Button>
            {' '}
            <Button color="secondary" onClick={this.togglePickupTimeModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        {/* Update Labour Pay Modal */}
        <Modal isOpen={this.state.labourPayModalOpen} toggle={this.toggleLabourPayModal}>
          <ModalHeader toggle={this.toggleLabourPayModal}>Add Pay</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label for="tripPayAmount">Trip Pay Amount *</Label>
              <Input
                type="number"
                name="tripPayAmount"
                id="tripPayAmount"
                min="0"
                step="0.01"
                value={this.state.tripPayAmount}
                onChange={(e) => this.setState({ tripPayAmount: e.target.value })}
                placeholder="Enter trip pay amount"
              />
            </FormGroup>
            <FormGroup>
              <Label for="labourPayAmount">Labour Pay Amount *</Label>
              <Input
                type="number"
                name="labourPayAmount"
                id="labourPayAmount"
                min="0"
                step="0.01"
                value={this.state.labourPayAmount}
                onChange={(e) => this.setState({ labourPayAmount: e.target.value })}
                placeholder="Enter labour pay amount"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={this.handleUpdateLabourPay}
              disabled={this.state.isUpdatingLabourPay}
            >
              {this.state.isUpdatingLabourPay ? 'Saving...' : 'Save Pay'}
            </Button>
            {' '}
            <Button color="secondary" onClick={this.toggleLabourPayModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={this.state.markPaidModalOpen} toggle={this.toggleMarkPaidModal}>
          <ModalHeader toggle={this.toggleMarkPaidModal}>Select Payment Mode</ModalHeader>
          <ModalBody>
            Choose how this trip was paid.
          </ModalBody>
          <ModalFooter>
            <Button
              color="success"
              onClick={() => this.handleMarkAsPaid('cash')}
              disabled={this.state.isMarkingPaid}
            >
              {this.state.isMarkingPaid ? 'Marking...' : 'Cash'}
            </Button>
            <Button
              color="primary"
              onClick={() => this.handleMarkAsPaid('online')}
              disabled={this.state.isMarkingPaid}
            >
              {this.state.isMarkingPaid ? 'Marking...' : 'Online'}
            </Button>
            <Button color="secondary" onClick={this.toggleMarkPaidModal} disabled={this.state.isMarkingPaid}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        <CustomerDetailsModal
          isOpen={this.state.customerDetailsModalOpen}
          toggle={this.closeCustomerDetailsModal}
          customerId={currentTrip.customer_id}
          onViewAllCalls={(phone) => {
            this.closeCustomerDetailsModal();
            this.props.history.push(`/app/call-manager?view=call-logs&phone=${encodeURIComponent(phone || '')}`);
          }}
        />

        {/* Preview before sending a customer notification (Booking Summary / Driver Details) */}
        <Modal isOpen={this.state.customerPreviewModalOpen} toggle={this.closeCustomerSendPreview} className={s.waPreviewModal}>
          <ModalHeader toggle={this.closeCustomerSendPreview}>
            {this.state.customerPreviewType
              ? this.CUSTOMER_PREVIEW_CONFIG[this.state.customerPreviewType].title
              : 'Preview'}
          </ModalHeader>
          <ModalBody>
            {this.state.isLoadingCustomerPreview ? (
              <div className="text-center">
                <i className="fa fa-spinner fa-spin" /> Loading preview...
              </div>
            ) : (
              <>
                {((this.state.customerPreviewType === 'booking_summary' && this.state.notifStatus.confirmationSent) ||
                  (this.state.customerPreviewType === 'driver_details' && this.state.notifStatus.driverDetailsSent)) && (
                  <p className={s.waAlreadySentWarning}>
                    <i className="fa fa-exclamation-triangle" /> This message has already been sent to the customer.
                  </p>
                )}
                <p className={s.waPreviewNote}>
                  This is what the customer will receive. Nothing is sent until you confirm.
                </p>
                <div className={s.waCanvas}>
                  <div className={s.waBubble}>
                    {this.state.customerPreviewChannel === 'template' && this.state.customerPreviewImageUrl && (
                      <img
                        src={this.state.customerPreviewImageUrl}
                        alt={this.state.customerPreviewImageKind === 'vehicle' ? 'Vehicle photo' : 'Message header'}
                        className={s.waHeaderImage}
                      />
                    )}
                    <div className={s.waBody}>
                      {this.renderWhatsAppText(this.state.customerPreviewMessage)}
                      <span className={s.waTime}>
                        {new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                  {this.state.customerPreviewChannel !== 'template' && this.state.customerPreviewImageUrl && (
                    <div className={s.waBubble}>
                      <img
                        src={this.state.customerPreviewImageUrl}
                        alt="Vehicle photo"
                        className={s.waHeaderImage}
                      />
                    </div>
                  )}
                </div>
                {this.state.customerPreviewImageKind === 'logo' && (
                  <p className={s.waPreviewNote}>
                    No vehicle photo on file, so the UNLOADIN logo is sent in the header.
                  </p>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={this.confirmCustomerSend}
              disabled={
                this.state.isLoadingCustomerPreview ||
                this.state.isSendingBookingSummary ||
                this.state.isSendingDriverDetails ||
                !this.state.customerPreviewMessage
              }
            >
              {this.state.isSendingBookingSummary || this.state.isSendingDriverDetails ? (
                <><i className="fa fa-spinner fa-spin" /> Sending...</>
              ) : (
                'Confirm & Send'
              )}
            </Button>
            {' '}
            <Button color="secondary" onClick={this.closeCustomerSendPreview}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    currentTrip: state.trips.currentTrip,
    isFetching: state.trips.isFetching,
  };
}

export default connect(mapStateToProps)(TripDetails);